import { useCallback, useEffect, useState } from 'react';
import {
  listNotifications,
  countNotifications,
  markAllNotificationsSeen,
  markNotificationSeen,
  type TaskNotification,
} from '../api/tasks';
import { useFeather } from './useFeather';
import './notifications.css';

const POLL_MS = 60_000;

const TYPE_META: Record<string, { icon: string; bg: string; color: string }> = {
  assigned: { icon: 'user-plus', bg: '#e7f1ff', color: '#1d4ed8' },
  mentioned: { icon: 'at-sign', bg: '#e0f2fe', color: '#0369a1' },
  status_changed: { icon: 'refresh-cw', bg: '#fef3c7', color: '#b45309' },
  completed: { icon: 'check', bg: '#dcfce7', color: '#15803d' },
  commented: { icon: 'message-circle', bg: '#e0f2fe', color: '#0369a1' },
  reacted: { icon: 'heart', bg: '#fce7f3', color: '#be185d' },
};
// P1 notifications carry no actor name, so the text is title-based.
const TYPE_LABEL: Record<string, string> = {
  assigned: 'Nový úkol',
  mentioned: 'Označení v úkolu',
  status_changed: 'Změna stavu úkolu',
  completed: 'Dokončený úkol',
  commented: 'Nový komentář',
  reacted: 'Reakce na komentář',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return 'právě teď';
  if (diffMin < 60) return `před ${diffMin} min`;
  if (diffMin < 1440) return `před ${Math.round(diffMin / 60)} h`;
  const days = Math.round(diffMin / 1440);
  if (days < 7) return `před ${days} dny`;
  return d.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

interface Props {
  onOpenTask: (taskId: number) => void;
  onOpenTasks: () => void;
}

export function NotificationBell({ onOpenTask, onOpenTasks }: Props) {
  const [count, setCount] = useState(0);
  const [list, setList] = useState<TaskNotification[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = () =>
      countNotifications()
        .then((r) => { if (!cancelled) setCount(r.count); })
        .catch(() => {});
    fetchCount();
    const timer = window.setInterval(fetchCount, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCount(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', fetchCount);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', fetchCount);
    };
  }, []);

  // Zvon = fronta NEPŘEČTENÝCH. Po „Označit vše" (i po kliku na položku) mají
  // notifikace zmizet a po znovuotevření se nevracet (Dan 2026-07-11), proto
  // zobrazujeme jen seen_at == null. Badge count = také nepřečtené → konzistentní.
  const loadList = useCallback(() => {
    setList(null);
    listNotifications()
      .then((rows) => setList(rows.filter((n) => n.seenAt == null)))
      .catch(() => setList([]));
  }, []);

  useFeather([list]);

  function handleItem(n: TaskNotification) {
    markNotificationSeen(n.id).catch(() => {});
    setCount((c) => Math.max(0, c - (n.seenAt == null ? 1 : 0)));
    // Přečtená položka mizí z fronty zvonu.
    setList((cur) => (cur ? cur.filter((x) => x.id !== n.id) : cur));
    onOpenTask(n.taskId);
  }

  function handleMarkAll(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    markAllNotificationsSeen().then(() => setCount(0)).catch(() => {});
    // Všechny nepřečtené jsou označené → fronta zvonu se vyprázdní.
    setList([]);
  }

  return (
    <div className="dropdown dropdown-notifications">
      <a
        href="#"
        className="btn btn-icon btn-rounded btn-flush-dark flush-soft-hover dropdown-toggle no-caret"
        data-bs-toggle="dropdown"
        role="button"
        aria-expanded="false"
        title={count > 0 ? `${count} nepřečtených` : 'Notifikace'}
        onClick={(e) => { e.preventDefault(); loadList(); }}
      >
        <span className="icon">
          <span className="position-relative">
            <span className="feather-icon"><i data-feather="bell"></i></span>
            {count > 0 && (
              <span className="badge badge-success badge-indicator position-top-end-overflow-1"></span>
            )}
          </span>
        </span>
      </a>
      <div className="dropdown-menu dropdown-menu-end">
        <div className="dropdown-header">
          <span>Notifikace</span>
          <button type="button" className="tn-mark-all" disabled={count === 0} onClick={handleMarkAll}>
            Označit vše
          </button>
        </div>
        <div className="dropdown-body">
          {list == null ? (
            <div className="tn-empty">načítám…</div>
          ) : list.length === 0 ? (
            <div className="tn-empty">Žádné nové notifikace</div>
          ) : (
            list.map((n) => {
              const meta = TYPE_META[n.type] ?? { icon: 'bell', bg: '#f3f4f6', color: '#6b7280' };
              return (
                <a
                  key={n.id}
                  className={`tn-item${n.seenAt == null ? ' tn-unseen' : ''}`}
                  role="button"
                  onClick={() => handleItem(n)}
                >
                  <span className="tn-icon" style={{ background: meta.bg, color: meta.color }}>
                    <i data-feather={meta.icon}></i>
                  </span>
                  <div className="tn-content">
                    <div className="tn-text">
                      {TYPE_LABEL[n.type] ?? 'Notifikace'}:{' '}
                      <strong>{n.taskTitle ?? `úkol #${n.taskId}`}</strong>
                    </div>
                    <div className="tn-meta">
                      <span>{formatTime(n.createdAt)}</span>
                    </div>
                  </div>
                </a>
              );
            })
          )}
        </div>
        <div className="dropdown-footer">
          <a role="button" onClick={(e) => { e.preventDefault(); onOpenTasks(); }}>
            Zobrazit všechny úkoly
          </a>
        </div>
      </div>
    </div>
  );
}
